import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Truck, CheckCircle2, XCircle, Loader2, Copy } from 'lucide-react';
import { toast } from 'sonner';

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('checking'); // checking, paid, failed
  const [data, setData] = useState(null);
  const polled = useRef(false);

  useEffect(() => {
    if (!sessionId || polled.current) return;
    polled.current = true;

    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        const d = res.data;
        if (d.payment_status === 'paid') {
          setStatus('paid');
          setData(d);
          return;
        }
        if (d.status === 'expired') {
          setStatus('failed');
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2500);
        } else {
          setStatus('failed');
        }
      } catch {
        attempts++;
        if (attempts < maxAttempts) setTimeout(poll, 2500);
        else setStatus('failed');
      }
    };
    poll();
  }, [sessionId]);

  const copyCode = () => {
    if (data?.master_code) {
      navigator.clipboard.writeText(data.master_code);
      toast.success('Master code copied!');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full">
        {/* Nav */}
        <div className="flex items-center gap-2 justify-center mb-10">
          <div className="bg-blue-600 p-1.5 rounded-lg">
            <Truck className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-lg text-white tracking-tight">Urbanvision Logistics</span>
        </div>

        {status === 'checking' && (
          <Card className="text-center" data-testid="payment-checking">
            <CardContent className="py-12 px-8">
              <Loader2 className="w-14 h-14 text-blue-500 mx-auto mb-4 animate-spin" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">Processing Payment...</h2>
              <p className="text-slate-500 text-sm">Please wait while we confirm your payment.</p>
            </CardContent>
          </Card>
        )}

        {status === 'paid' && (
          <Card className="text-center" data-testid="payment-paid">
            <CardContent className="py-12 px-8">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Payment Successful!</h2>
              <p className="text-slate-600 mb-6">Your franchise has been created. Here is your master code:</p>

              {data?.master_code && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6" data-testid="master-code-display">
                  <p className="text-sm text-blue-600 font-medium mb-1">Your Master Code</p>
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-4xl font-extrabold tracking-widest text-blue-700">{data.master_code}</span>
                    <button onClick={copyCode} className="p-2 hover:bg-blue-100 rounded-lg transition-colors" data-testid="copy-code-btn">
                      <Copy className="w-5 h-5 text-blue-500" />
                    </button>
                  </div>
                </div>
              )}

              <p className="text-sm text-slate-500 mb-6">
                Use this code to log in and set up your franchise. An email with these details has been sent to the administrator.
              </p>

              <a href="/auth">
                <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold h-11" data-testid="go-to-login-btn">
                  Go to Login
                </Button>
              </a>
            </CardContent>
          </Card>
        )}

        {status === 'failed' && (
          <Card className="text-center" data-testid="payment-failed">
            <CardContent className="py-12 px-8">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Payment Not Completed</h2>
              <p className="text-slate-600 mb-6">
                Your payment could not be confirmed. No charges were made. Please try again or contact us for help.
              </p>
              <a href="/pricing">
                <Button variant="outline" className="w-full" data-testid="back-to-pricing-btn">Back to Pricing</Button>
              </a>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
